import { useEffect, useState } from 'react'

import { Check, Download, RotateCcw } from 'lucide-react'
import { z } from 'zod'

import { Database, spreadsheetFileTypes } from '@fortem-monorepo/constants'
import { CustomAgGrid, getBasicColDefsFromData } from '@fortem-monorepo/react-ag'
import { GridWidget, useFormWithSchema } from '@fortem-monorepo/react-spa'
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogProps,
  DialogTitle,
  FileUploadInput,
  Form,
  FormError,
  FormField,
  FormItem,
  FormLabel,
  LoadingButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@fortem-monorepo/react-ui'
import { Trades } from '@fortem-monorepo/web-sdk'

import {
  useBookUploadedTradesMutation,
  useCheckUploadedTradesMutation,
  useDownloadTradeTemplateMutation,
} from '@/react-query/mutations/trades'

const DATABASES = ['old', 'new', 'both'] satisfies Database[]

const uploadTradeSchema = z
  .object({
    file: z.instanceof(FileList, { message: 'File is required.' }),
    database: z.enum(DATABASES),
  })
  .superRefine((data, ctx) => {
    const hasFile = (data.file ?? [])?.length > 0

    if (!hasFile) {
      ctx.addIssue({
        path: ['file'],
        code: 'custom',
        message: 'Upload at least one file here or in Fund Rating.',
      })
    }
  })

export interface UploadTradesDialogProps extends DialogProps {
  onClose: () => void
}

export const UploadTradesDialog: React.FC<UploadTradesDialogProps> = ({ onClose, ...props }) => {
  const form = useFormWithSchema({ mode: 'all', defaultValues: { database: 'both' } }, uploadTradeSchema)
  const checkUploadedTradesMutation = useCheckUploadedTradesMutation()
  const bookUploadedTradesMutation = useBookUploadedTradesMutation()
  const downloadTemplateMutation = useDownloadTradeTemplateMutation()
  const [checkedTradesData, setCheckedTradesData] = useState<Trades.CheckUploadedTradesData | null>(null)

  const selectedFile = form.watch('file')
  const checkedTrades = checkedTradesData?.output ?? []
  const uploadErrors = checkedTradesData?.errors ?? []

  useEffect(() => {
    form.trigger()
  }, [])

  const onCheck = form.handleSubmit(async (data) => {
    const checkedData = await checkUploadedTradesMutation.mutateAsync({ file: data.file })
    setCheckedTradesData(checkedData)
  })

  const onBook = form.handleSubmit(async (data) => {
    await bookUploadedTradesMutation.mutateAsync({
      database: data.database,
      trades: checkedTrades,
    })
    onClose()
  })

  const resetCheckedTrades = () => {
    setCheckedTradesData(null)
  }

  return (
    <Dialog
      {...props}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
        <Form {...form}>
          <DialogHeader>
            <DialogTitle>Upload Trades</DialogTitle>
            <DialogDescription>Check a completed trade template, then book the parsed trades.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div>
              <p>Trades uploaded by file must use the downloaded template.</p>
              <p>
                Accepted file types: <span>.csv, .xlsx</span>
              </p>
            </div>
            <LoadingButton
              icon={<Download />}
              size="sm"
              type="button"
              variant="outline"
              onClick={async () => {
                await downloadTemplateMutation.mutateAsync()
              }}
            >
              Template
            </LoadingButton>
          </div>
          <FormField
            name="database"
            control={form.control}
            render={({ field }) => (
              <FormItem className="w-full max-w-48">
                <div className="flex items-center gap-2">
                  <FormLabel>Database</FormLabel>
                  <FormError />
                </div>
                <Select value={field.value} onValueChange={(val) => form.setValue('database', val as Database)}>
                  <SelectTrigger className="w-full" onBlur={field.onBlur} ref={field.ref}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="old">Old DB</SelectItem>
                    <SelectItem value="new">New DB</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            name="file"
            control={form.control}
            render={() => {
              const props = form.register('file')
              return (
                <FormItem>
                  <div className="flex items center gap-1">
                    <FormLabel>File</FormLabel>
                    <FormError />
                  </div>
                  <FileUploadInput
                    {...props}
                    accept={spreadsheetFileTypes.join(',')}
                    multiple={false}
                    files={selectedFile}
                    onChange={(e) => {
                      props.onChange(e)
                      resetCheckedTrades()
                      form.trigger(['file'])
                    }}
                    setValue={(val) => {
                      form.setValue('file', val, { shouldValidate: true })
                      resetCheckedTrades()
                      form.trigger(['file'])
                    }}
                  />
                </FormItem>
              )
            }}
          />
          {checkedTradesData && (
            <div className="space-y-4 overflow-hidden">
              {uploadErrors.length === 0 && (
                <p role="status" className="flex items-center gap-1.5 text-sm text-emerald-600">
                  <Check size={16} />
                  Parsing successful, showing {checkedTrades.length} trade{checkedTrades.length === 1 ? '' : 's'}
                </p>
              )}
              {uploadErrors.length > 0 && (
                <Alert variant="destructive" className={'flex flex-col'}>
                  <div className="flex flex-wrap items-center gap-2 text-sm mb-2">
                    <Badge theme={uploadErrors.length > 0 ? 'destructive' : 'success'} variant="outline">
                      {uploadErrors.length} error{uploadErrors.length === 1 ? '' : 's'}
                    </Badge>
                  </div>
                  <AlertDescription>
                    <ul className="max-h-48 overflow-y-auto list-disc pl-4">
                      {uploadErrors.map((error, index) => (
                        <li key={index}>
                          Row {error.row}, {error.field}: {error.message}
                        </li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              <TradeUploadPreviewTable
                title="Successful rows"
                data={checkedTradesData.input}
                emptyMessage="No uploaded rows were returned."
              />
            </div>
          )}
          <DialogFooter>
            <div className="flex flex-wrap items-center gap-2 justify-self-end">
              <Button size="sm" type="button" variant="destructive" onClick={onClose}>
                Cancel
              </Button>
              {checkedTradesData && (
                <Button size="sm" type="button" variant="outline" onClick={resetCheckedTrades}>
                  <RotateCcw />
                  Change file
                </Button>
              )}
              {!checkedTradesData ? (
                <LoadingButton
                  size="sm"
                  type="button"
                  variant="default"
                  disabled={!form.formState.isValid || form.formState.isSubmitting}
                  onClick={async () => onCheck()}
                >
                  Check trades
                </LoadingButton>
              ) : (
                <LoadingButton
                  size="sm"
                  type="button"
                  variant="default"
                  disabled={checkedTrades.length === 0 || uploadErrors.length > 0}
                  onClick={async () => onBook()}
                >
                  Book trades
                </LoadingButton>
              )}
            </div>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

interface TradeUploadPreviewTableProps {
  title: string
  data: Record<string, unknown>[]
  emptyMessage: string
}

const TradeUploadPreviewTable: React.FC<TradeUploadPreviewTableProps> = ({ title, data, emptyMessage }) => {
  return (
    <GridWidget title={title} className="min-w-0">
      {data.length === 0 ? (
        <div className="p-4 text-sm text-muted-foreground">{emptyMessage}</div>
      ) : (
        <div className="h-72">
          <CustomAgGrid
            domLayout="normal"
            rowData={data}
            columnDefs={getBasicColDefsFromData(data)}
            defaultColDef={{
              minWidth: 140,
              editable: false,
              valueFormatter: ({ value }) => formatPreviewValue(value),
            }}
          />
        </div>
      )}
    </GridWidget>
  )
}

const formatPreviewValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return '-'
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  if (typeof value === 'object') {
    return JSON.stringify(value)
  }

  return String(value)
}
